import { firestore } from "../firebase";
import DB_PATH from "../DB_PATH";
import {
  CommunityMediaConfig,
  CommunityFormatProfile,
  CommunityFormatProfileEntry,
  CommunityCustomFormat,
  CommunityVisualStyleProfile,
  CommunityVisualStyleReference,
  CommunityAdFormat,
  CommunityAdFormatProfile,
  CommunityAdFormatProfileEntry,
  CompetitorReferenceAd,
  AdPlatform,
  AdAssetType,
  VoiceOfCustomer,
  VoiceOfCustomerQuote,
} from "../../types/CommunityMediaConfig";

export type { CommunityMediaConfig };

// Audience research (Gemini + Google Search, runs in-browser using the
// existing REACT_APP_GEMINI_API_KEY — same pattern OnboardingAPI uses).
export {
  researchCommunityAudience,
  researchFormatProfileForAudience,
  researchVisualStyleForAudience,
  researchFormatElaboration,
  researchAdFormatsForAudience,
} from "./researchAudience";
export type {
  ResearchAudienceInput,
  ResearchAudienceResult,
  ResearchFormatProfileInput,
  ResearchFormatProfileResult,
  ResearchVisualStyleInput,
  ResearchVisualStyleResult,
  ResearchFormatElaborationInput,
  ResearchFormatElaborationResult,
  ResearchAdFormatProfileInput,
  ResearchAdFormatProfileResult,
  DiscoveredAdFormat,
} from "./researchAudience";

/**
 * Unified per-community config for everything that drives media generation:
 * audience voice, brand/writing style, visual style, buyer persona, and
 * future additions (e.g. reference image stacks, banned topics, brand colors
 * for slide rendering).
 *
 * Lives at: Community/{id}/CommunityMedia/mediaConfig
 *
 * The root Community doc stays trimmed to essentials. Generators read this
 * doc alongside the existing slide/reel/video-library docs.
 */
const MEDIA_CONFIG_DOC_ID = "mediaConfig";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const sanitizeVocQuote = (q: unknown): VoiceOfCustomerQuote | null => {
  if (!isRecord(q)) return null;
  const text = typeof q.text === "string" ? q.text.trim() : "";
  if (!text) return null;
  const source =
    typeof q.source === "string" && q.source.trim() ? q.source.trim() : undefined;
  const tag = q.tag as VoiceOfCustomerQuote["tag"];
  return { text, source, tag };
};

const normalizeVoiceOfCustomer = (value: unknown): VoiceOfCustomer | undefined => {
  if (!isRecord(value)) return undefined;
  const quotesIn = Array.isArray(value.quotes) ? value.quotes : [];
  const quotes = quotesIn
    .map(sanitizeVocQuote)
    .filter((q): q is VoiceOfCustomerQuote => q !== null);
  const notes =
    typeof value.notes === "string" && value.notes.trim() ? value.notes : "";
  const lastResearchAt =
    typeof value.lastResearchAt === "string" ? value.lastResearchAt : undefined;
  return { quotes, notes, lastResearchAt };
};

const sanitizeFormatProfileEntry = (
  value: unknown
): CommunityFormatProfileEntry | null => {
  if (!isRecord(value)) return null;
  const format = typeof value.format === "string" ? value.format.trim() : "";
  if (!format) return null;
  const weightRaw =
    typeof value.weight === "number" ? value.weight : Number(value.weight);
  const weight = Number.isFinite(weightRaw)
    ? Math.max(0, Math.min(100, weightRaw))
    : 0;
  const evidence =
    typeof value.evidence === "string" && value.evidence.trim()
      ? value.evidence.trim()
      : undefined;
  return { format, weight, evidence };
};

const sanitizeCustomFormat = (
  value: unknown
): CommunityCustomFormat | null => {
  if (!isRecord(value)) return null;
  const key = typeof value.key === "string" ? value.key.trim() : "";
  const label = typeof value.label === "string" ? value.label.trim() : "";
  const structure =
    typeof value.structure === "string" ? value.structure.trim() : "";
  if (!key || !label || !structure) return null;
  const icon =
    typeof value.icon === "string" && value.icon.trim()
      ? value.icon.trim()
      : undefined;
  const lever =
    typeof value.lever === "string" && value.lever.trim()
      ? value.lever.trim()
      : undefined;
  const evidence =
    typeof value.evidence === "string" && value.evidence.trim()
      ? value.evidence.trim()
      : undefined;
  const source =
    value.source === "manual" || value.source === "research"
      ? value.source
      : "research";
  const createdAt =
    typeof value.createdAt === "string" && value.createdAt.trim()
      ? value.createdAt.trim()
      : new Date().toISOString();
  return { key, label, icon, structure, lever, evidence, source, createdAt };
};

const normalizeCustomFormats = (
  value: unknown
): CommunityCustomFormat[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const seen = new Set<string>();
  const out: CommunityCustomFormat[] = [];
  value.forEach((entry) => {
    const sanitized = sanitizeCustomFormat(entry);
    if (!sanitized) return;
    if (seen.has(sanitized.key)) return;
    seen.add(sanitized.key);
    out.push(sanitized);
  });
  return out.length > 0 ? out : undefined;
};

const normalizeVisualStyleProfile = (
  value: unknown
): CommunityVisualStyleProfile | undefined => {
  if (!isRecord(value)) return undefined;
  const description =
    typeof value.description === "string" ? value.description.trim() : "";
  const tagsIn = Array.isArray(value.tags) ? value.tags : [];
  const tags = tagsIn
    .map((t) => (typeof t === "string" ? t.trim() : ""))
    .filter((t): t is string => Boolean(t));
  const refsIn = Array.isArray(value.references) ? value.references : [];
  const references: CommunityVisualStyleReference[] = [];
  refsIn.forEach((entry) => {
    if (!isRecord(entry)) return;
    const account =
      typeof entry.account === "string" ? entry.account.trim() : "";
    const observation =
      typeof entry.observation === "string" ? entry.observation.trim() : "";
    if (!account || !observation) return;
    references.push({ account, observation });
  });
  const lastResearchAt =
    typeof value.lastResearchAt === "string" ? value.lastResearchAt : undefined;
  if (!description && tags.length === 0) return undefined;
  return {
    description,
    tags,
    ...(references.length ? { references } : {}),
    ...(lastResearchAt ? { lastResearchAt } : {}),
  };
};

const normalizeFormatProfile = (
  value: unknown
): CommunityFormatProfile | undefined => {
  if (!isRecord(value)) return undefined;
  const formatsIn = Array.isArray(value.formats) ? value.formats : [];
  const formats = formatsIn
    .map(sanitizeFormatProfileEntry)
    .filter((e): e is CommunityFormatProfileEntry => e !== null)
    .sort((a, b) => b.weight - a.weight);
  if (formats.length === 0) return undefined;
  const lastResearchAt =
    typeof value.lastResearchAt === "string" ? value.lastResearchAt : undefined;
  return { formats, lastResearchAt };
};

const VALID_AD_PLATFORMS: AdPlatform[] = ["meta", "tiktok"];
const VALID_AD_ASSET_TYPES: AdAssetType[] = [
  "ugc-testimonial",
  "founder-talking-head",
  "problem-agitate-solve",
  "listicle",
  "before-after",
  "day-in-the-life",
  "pov-i-tried",
  "static-image",
  "carousel",
];

const sanitizeAdFormat = (value: unknown): CommunityAdFormat | null => {
  if (!isRecord(value)) return null;
  const key = typeof value.key === "string" ? value.key.trim() : "";
  const label = typeof value.label === "string" ? value.label.trim() : "";
  const structure =
    typeof value.structure === "string" ? value.structure.trim() : "";
  if (!key || !label || !structure) return null;

  const platformsIn = Array.isArray(value.platforms) ? value.platforms : [];
  const platforms = platformsIn
    .map((p) => (typeof p === "string" ? p.trim().toLowerCase() : ""))
    .filter((p): p is AdPlatform => (VALID_AD_PLATFORMS as string[]).includes(p));
  if (platforms.length === 0) return null;

  const assetTypeRaw =
    typeof value.assetType === "string" ? value.assetType.trim() : "";
  const assetType = (VALID_AD_ASSET_TYPES as string[]).includes(assetTypeRaw)
    ? (assetTypeRaw as AdAssetType)
    : "ugc-testimonial";

  const icon =
    typeof value.icon === "string" && value.icon.trim() ? value.icon.trim() : undefined;
  const lever =
    typeof value.lever === "string" && value.lever.trim() ? value.lever.trim() : undefined;
  const evidence =
    typeof value.evidence === "string" && value.evidence.trim() ? value.evidence.trim() : undefined;

  const lenRaw =
    typeof value.targetLengthSec === "number"
      ? value.targetLengthSec
      : Number(value.targetLengthSec);
  const targetLengthSec = Number.isFinite(lenRaw)
    ? Math.max(3, Math.min(60, Math.round(lenRaw)))
    : undefined;

  const source = value.source === "manual" ? "manual" : "research";
  const createdAt =
    typeof value.createdAt === "string" && value.createdAt.trim()
      ? value.createdAt.trim()
      : new Date().toISOString();

  return {
    key,
    label,
    icon,
    platforms: Array.from(new Set(platforms)) as AdPlatform[],
    assetType,
    structure,
    lever,
    evidence,
    targetLengthSec,
    source,
    createdAt,
  };
};

const normalizeAdFormats = (
  value: unknown
): CommunityAdFormat[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const seen = new Set<string>();
  const out: CommunityAdFormat[] = [];
  value.forEach((entry) => {
    const sanitized = sanitizeAdFormat(entry);
    if (!sanitized) return;
    if (seen.has(sanitized.key)) return;
    seen.add(sanitized.key);
    out.push(sanitized);
  });
  return out.length > 0 ? out : undefined;
};

const sanitizeAdFormatProfileEntry = (
  value: unknown
): CommunityAdFormatProfileEntry | null => {
  if (!isRecord(value)) return null;
  const format = typeof value.format === "string" ? value.format.trim() : "";
  if (!format) return null;
  const weightRaw =
    typeof value.weight === "number" ? value.weight : Number(value.weight);
  const weight = Number.isFinite(weightRaw)
    ? Math.max(0, Math.min(100, weightRaw))
    : 0;
  const evidence =
    typeof value.evidence === "string" && value.evidence.trim()
      ? value.evidence.trim()
      : undefined;
  return { format, weight, evidence };
};

const sanitizeCompetitorReferenceAd = (
  value: unknown
): CompetitorReferenceAd | null => {
  if (!isRecord(value)) return null;
  const imageURL =
    typeof value.imageURL === "string" ? value.imageURL.trim() : "";
  if (!imageURL) return null;
  const id = typeof value.id === "string" && value.id.trim()
    ? value.id.trim()
    : imageURL;
  const label =
    typeof value.label === "string" && value.label.trim()
      ? value.label.trim()
      : undefined;
  const source =
    typeof value.source === "string" && value.source.trim()
      ? value.source.trim()
      : undefined;
  const platformRaw =
    typeof value.platform === "string" ? value.platform.trim().toLowerCase() : "";
  const platform =
    platformRaw === "meta" || platformRaw === "tiktok"
      ? (platformRaw as AdPlatform)
      : undefined;
  const createdAt =
    typeof value.createdAt === "string" && value.createdAt.trim()
      ? value.createdAt.trim()
      : new Date().toISOString();
  return { id, imageURL, label, source, platform, createdAt };
};

const normalizeCompetitorReferenceAds = (
  value: unknown
): CompetitorReferenceAd[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const seen = new Set<string>();
  const out: CompetitorReferenceAd[] = [];
  value.forEach((entry) => {
    const sanitized = sanitizeCompetitorReferenceAd(entry);
    if (!sanitized) return;
    if (seen.has(sanitized.id)) return;
    seen.add(sanitized.id);
    out.push(sanitized);
  });
  return out.length > 0 ? out : undefined;
};

const normalizeAdFormatProfile = (
  value: unknown
): CommunityAdFormatProfile | undefined => {
  if (!isRecord(value)) return undefined;
  const formatsIn = Array.isArray(value.formats) ? value.formats : [];
  const formats = formatsIn
    .map(sanitizeAdFormatProfileEntry)
    .filter((e): e is CommunityAdFormatProfileEntry => e !== null)
    .sort((a, b) => b.weight - a.weight);
  if (formats.length === 0) return undefined;
  const lastResearchAt =
    typeof value.lastResearchAt === "string" ? value.lastResearchAt : undefined;
  return { formats, lastResearchAt };
};

export const normalizeCommunityMediaConfig = (
  value: unknown
): CommunityMediaConfig => {
  if (!isRecord(value)) return {};
  const out: CommunityMediaConfig = {};
  if (typeof value.writingStyle === "string") out.writingStyle = value.writingStyle;
  if (typeof value.visualStyle === "string") out.visualStyle = value.visualStyle;
  if (typeof value.buyerPersona === "string") out.buyerPersona = value.buyerPersona;
  const voc = normalizeVoiceOfCustomer(value.voiceOfCustomer);
  if (voc) out.voiceOfCustomer = voc;
  const profile = normalizeFormatProfile(value.formatProfile);
  if (profile) out.formatProfile = profile;
  const customFormats = normalizeCustomFormats(value.customFormats);
  if (customFormats) out.customFormats = customFormats;
  const visualStyleProfile = normalizeVisualStyleProfile(value.visualStyleProfile);
  if (visualStyleProfile) out.visualStyleProfile = visualStyleProfile;
  const adFormatProfile = normalizeAdFormatProfile(value.adFormatProfile);
  if (adFormatProfile) out.adFormatProfile = adFormatProfile;
  const adFormats = normalizeAdFormats(value.adFormats);
  if (adFormats) out.adFormats = adFormats;
  const competitorReferenceAds = normalizeCompetitorReferenceAds(
    value.competitorReferenceAds
  );
  if (competitorReferenceAds) out.competitorReferenceAds = competitorReferenceAds;
  return out;
};

export const getCommunityMediaConfigRef = (communityId: string) =>
  firestore
    .collection(DB_PATH.COMMUNITY)
    .doc(communityId)
    .collection(DB_PATH.COMMUNITY_MEDIA)
    .doc(MEDIA_CONFIG_DOC_ID);

export const getCommunityMediaConfig = async (
  communityId: string,
  options?: { communityData?: unknown }
): Promise<CommunityMediaConfig> => {
  if (!communityId) return {};

  const ref = getCommunityMediaConfigRef(communityId);
  const snap = await ref.get();
  const config = normalizeCommunityMediaConfig(snap.data());

  // Backward compatibility: if the new doc has no value for legacy fields,
  // fall back to whatever lives on the root Community doc (writingStyle,
  // visualStyle, buyerPersona). Generators still read from the root doc
  // today, so we mirror that lookup here until migration finishes.
  if (
    !config.writingStyle ||
    !config.visualStyle ||
    !config.buyerPersona
  ) {
    const communityData =
      options?.communityData === undefined
        ? (
            await firestore.collection(DB_PATH.COMMUNITY).doc(communityId).get()
          ).data()
        : options.communityData;

    if (isRecord(communityData)) {
      if (!config.writingStyle && typeof communityData.writingStyle === "string") {
        config.writingStyle = communityData.writingStyle;
      }
      if (!config.visualStyle) {
        const visual =
          typeof communityData.slideVisualStyle === "string"
            ? communityData.slideVisualStyle
            : typeof communityData.visualStyle === "string"
            ? communityData.visualStyle
            : undefined;
        if (visual) config.visualStyle = visual;
      }
      if (!config.buyerPersona && typeof communityData.buyerPersona === "string") {
        config.buyerPersona = communityData.buyerPersona;
      }
    }
  }

  return config;
};

export const saveCommunityMediaConfig = async (
  communityId: string,
  patch: Partial<CommunityMediaConfig>
): Promise<void> => {
  if (!communityId) return;
  const normalized = normalizeCommunityMediaConfig({
    ...patch,
  });
  await getCommunityMediaConfigRef(communityId).set(normalized, { merge: true });
};

export const saveVoiceOfCustomer = async (
  communityId: string,
  voc: VoiceOfCustomer
): Promise<void> => {
  if (!communityId) return;
  await saveCommunityMediaConfig(communityId, { voiceOfCustomer: voc });
};

export const buildVisualStylePromptBlock = (
  profile: CommunityVisualStyleProfile | undefined,
  legacyVisualStyle?: string
): string => {
  if (profile && (profile.description?.trim() || profile.tags.length > 0)) {
    const lines: string[] = [];
    if (profile.description?.trim()) {
      lines.push(profile.description.trim());
    }
    if (profile.tags.length > 0) {
      lines.push(`Style cues: ${profile.tags.join(", ")}.`);
    }
    return lines.join(" ");
  }
  if (legacyVisualStyle?.trim()) return legacyVisualStyle.trim();
  return "";
};

export const buildVoiceOfCustomerPromptBlock = (
  voc: VoiceOfCustomer | undefined
): string => {
  if (!voc) return "";
  const quoteLines = (voc.quotes || [])
    .filter((q) => q.text?.trim())
    .map((q) => {
      const tag = q.tag ? `[${q.tag}] ` : "";
      const src = q.source ? ` (${q.source})` : "";
      return `- ${tag}"${q.text.trim()}"${src}`;
    });
  const notes = voc.notes?.trim();

  if (!quoteLines.length && !notes) return "";

  const sections = [
    "AUDIENCE VOICE (verbatim phrases the audience actually uses — mirror this language in titles, scripts, and hooks):",
    ...(quoteLines.length ? quoteLines : []),
    ...(notes ? ["", `Additional notes: ${notes}`] : []),
  ];

  return sections.join("\n");
};
